"use client"

import { useEffect } from "react"
import { usePathname, useRouter } from "next/navigation"
import { defaultShouldDehydrateQuery } from "@tanstack/react-query"
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client"

import { me } from "@/lib/api/auth"
import { setUnauthorizedHandler } from "@/lib/api/client"
import { getConfig } from "@/lib/api/config"
import { toMember } from "@/lib/api/map"
import { qk } from "@/lib/api/query-keys"
import { accessTokenStore } from "@/lib/api/token-store"
import {
  purgeQueryCache,
  queryClient,
  queryPersister,
} from "@/lib/query-client"
import { BackButtonController } from "@/components/back-button-controller"
// Side-effect import: captures the `beforeinstallprompt` event at module
// init so it isn't missed when the install banner mounts late.
import "@/lib/install-prompt-listener"
import { profileIncomplete, useAuthStore } from "@/lib/store/auth"
import { useHydrated } from "@/lib/use-hydrated"
import {
  clearUser,
  getErrorBoundary,
  identifyMember,
} from "@/lib/observability/bugsnag"

// Routes that should NOT trigger a redirect when onboarding is unfinished.
// /complete-data and /profile-setup are the destinations; /tutorial runs AFTER
// both; /sign-in and /sign-up are entry points the user might land on while
// logging in / switching accounts.
const ONBOARDING_EXEMPT = [
  "/complete-data",
  "/invitation",
  "/profile-setup",
  "/tutorial",
  "/sign-in",
  "/sign-up",
  "/forgot-password",
]

/**
 * Keeps a half-onboarded member on the screen they still owe us, whatever URL
 * they type. Two stages, in order:
 *
 * 1. NO verified contact channel at all -> /complete-data. A single verified
 *    channel (email OR phone) is sufficient — second-channel verification is
 *    optional and offered later, not blocking. The gate reads *_verified_at so
 *    an optimistic update of the raw email/phone column can't bypass it.
 * 2. Profile setup never finished -> /profile-setup.
 *
 * One guard rather than a check per page: every route is covered, and the two
 * stages can't race each other into a double redirect.
 */
function OnboardingGuard() {
  const router = useRouter()
  const pathname = usePathname()
  const hydrated = useHydrated()
  const member = useAuthStore((s) => s.member)

  useEffect(() => {
    if (!hydrated || !member) return
    if (ONBOARDING_EXEMPT.some((p) => pathname?.startsWith(p))) return

    if (!member.emailVerifiedAt && !member.phoneVerifiedAt) {
      router.replace("/complete-data")
      return
    }
    if (profileIncomplete(member)) router.replace("/profile-setup")
  }, [hydrated, member, pathname, router])

  return null
}

/** Syncs the signed-in member's ID (only) onto Bugsnag events. Placed here so it
 *  runs after instrumentation-client has started Bugsnag, and re-applies when the
 *  persisted member is rehydrated from localStorage after boot. */
function BugsnagUserSync() {
  const member = useAuthStore((s) => s.member)
  useEffect(() => {
    if (member) identifyMember(member.id)
    else clearUser()
  }, [member])
  return null
}

/** In-shell fallback shown when a client component throws during render. */
function ErrorFallback({ clearError }: { clearError: () => void }) {
  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-4 p-8 text-center">
      <div>
        <p className="text-lg font-semibold text-foreground">
          Something went wrong
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          This screen hit an unexpected error.
        </p>
      </div>
      <button
        type="button"
        onClick={clearError}
        className="rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground"
      >
        Try again
      </button>
    </div>
  )
}

/** Wraps the app in the Bugsnag React ErrorBoundary once it exists. It's null
 *  during SSR and the pre-start client render, where children render unwrapped —
 *  the boundary adds no DOM of its own, so the hydrated markup is identical. */
function AppErrorBoundary({ children }: { children: React.ReactNode }) {
  const Boundary = getErrorBoundary()
  if (!Boundary) return <>{children}</>
  // getErrorBoundary() returns a module-level singleton created once at startup
  // (before hydration) — a stable reference, so rendering it dynamically here
  // never remounts the tree. The lint rule can't prove that, so disable it.
  // eslint-disable-next-line react-hooks/static-components
  return <Boundary FallbackComponent={ErrorFallback}>{children}</Boundary>
}

/**
 * Mounts the react-query client and bootstraps the auth session:
 * - registers the global 401 handler (force logout + redirect)
 * - on launch, if a token exists, validates it via /auth/me and refreshes the member
 * - gates half-onboarded accounts to /complete-data or /profile-setup via OnboardingGuard
 */
export function AppProviders({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    setUnauthorizedHandler(() => {
      // Session dead — clear local state and bounce to sign-in (no server call to avoid a loop).
      accessTokenStore.clear()
      useAuthStore.setState({ member: null, signupDraft: null })
      // Drop the previous session's cached membership/bookings so they can't
      // flash for whoever signs in next on this device.
      purgeQueryCache()
      if (
        typeof window !== "undefined" &&
        !window.location.pathname.startsWith("/sign-in")
      ) {
        window.location.assign("/sign-in")
      }
    })

    if (accessTokenStore.get()) {
      me()
        .then((user) => useAuthStore.getState().setMember(toMember(user)))
        .catch(() => {
          // 401 is handled by the unauthorized handler above; ignore other errors here.
        })

      // Warm the feature flags alongside the identity call so the booking
      // screens never render a payment option the server has switched off and
      // then snatch it away a moment later.
      queryClient
        .prefetchQuery({ queryKey: qk.config(), queryFn: getConfig })
        .catch(() => {})
    }

    return () => setUnauthorizedHandler(null)
  }, [])

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: queryPersister,
        maxAge: 1000 * 60 * 60 * 24, // 24h — stale dashboard shows, then revalidates
        // New deploy → discard the old cache (shape may have changed).
        buster: process.env.NEXT_PUBLIC_APP_VERSION ?? "dev",
        dehydrateOptions: {
          shouldDehydrateQuery: (query) => {
            const [head, , sub] = query.queryKey
            // Never persist short-lived QR tokens (120s) or date-specific
            // availability — both would render stale/wrong on reopen.
            if (head === "qr") return false
            if (head === "courts" && sub === "availability") return false
            // Purchase status flips fast (waiting_for_payment → completed via
            // webhook/poll); a persisted "waiting" would flash stale on reopen.
            if (head === "purchases") return false
            return defaultShouldDehydrateQuery(query)
          },
        },
      }}
    >
      <BugsnagUserSync />
      <OnboardingGuard />
      <BackButtonController />
      <AppErrorBoundary>{children}</AppErrorBoundary>
    </PersistQueryClientProvider>
  )
}
